import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '@/services/api';

// Role hierarchy for permission checking
const ROLE_HIERARCHY: Record<string, number> = {
  'user': 1,
  'admin': 2,
  'super_admin': 3,
};

interface UserProfile {
  id: string;
  email: string;
  role: string;
  companyId: string;
}

interface RequireRoleProps {
  children: React.ReactNode;
  requiredRole: 'user' | 'admin' | 'super_admin';
  redirectTo?: string;
}

/**
 * Route guard component that checks if the user has the required role.
 * Redirects to the specified path (or dashboard) if access is denied.
 */
export const RequireRole: React.FC<RequireRoleProps> = ({
  children,
  requiredRole,
  redirectTo = '/dashboard',
}) => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Fetch current user's profile from API
        const response = await api.get<UserProfile>('/users/me');

        if (response.success && response.data) {
          const userLevel = ROLE_HIERARCHY[response.data.role] || 0;
          const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
          setHasAccess(userLevel >= requiredLevel);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error checking role access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [requiredRole]);

  // Show nothing while loading
  if (loading) {
    return null;
  }

  // If no access, redirect
  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
